import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon, { HelpHint } from '../common/SafeIcon';
import {
  FiPlus, FiSettings, FiActivity, FiGlobe, FiX, FiRefreshCw,
  FiSave, FiAlertCircle, FiShield, FiCopy
} from 'react-icons/fi';
import { gatewayApi } from '../services/gateway';

const showToast = (message, type = 'success') => {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
};

const blankForm = {
  id: '',
  name: '',
  clientId: '',
  status: 'active',
  origins: '',
  provider: 'Emailit',
  monthlyLimit: 0,
  fromName: '',
  fromEmail: '',
  replyToEmail: '',
  allowedRecipients: '',
  allowAnyRecipient: false,
  allowDynamicReplyTo: false,
  rateLimitPerMinute: 30,
  notes: ''
};

const Apps = () => {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(blankForm);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [appsResponse, clientsResponse] = await Promise.all([
        gatewayApi.listApps(),
        gatewayApi.listClients()
      ]);
      setApps(appsResponse.data || []);
      setClients(clientsResponse.data || []);
    } catch (err) {
      setError(err.message || 'Unable to load applications.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const clientMap = useMemo(
    () => Object.fromEntries(clients.map(client => [client.id, client.name])),
    [clients]
  );

  const filteredApps = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return apps;
    return apps.filter(app =>
      [app.name, clientMap[app.clientId], app.origins, app.provider, app.status]
        .some(value => String(value || '').toLowerCase().includes(term))
    );
  }, [apps, clientMap, searchTerm]);

  const openCreate = () => {
    setForm({
      ...blankForm,
      clientId: clients[0]?.id || ''
    });
    setEditorOpen(true);
  };

  const openEdit = (app) => {
    setForm({
      id: app.id,
      name: app.name || '',
      clientId: app.clientId || '',
      status: app.status || 'active',
      origins: app.origins || '',
      provider: app.provider || 'Emailit',
      monthlyLimit: app.monthlyLimit || 0,
      fromName: app.fromName || '',
      fromEmail: app.fromEmail || '',
      replyToEmail: app.replyToEmail || '',
      allowedRecipients: app.allowedRecipients || '',
      allowAnyRecipient: Boolean(app.allowAnyRecipient),
      allowDynamicReplyTo: Boolean(app.allowDynamicReplyTo),
      rateLimitPerMinute: app.rateLimitPerMinute || 30,
      notes: app.notes || ''
    });
    setEditorOpen(true);
  };

  const saveApp = async () => {
    if (!form.name.trim() || !form.clientId) {
      showToast('App name and client are required.', 'error');
      return;
    }
    if (!form.origins.trim()) {
      showToast('At least one allowed origin is required.', 'error');
      return;
    }
    if (!form.allowAnyRecipient && !form.allowedRecipients.trim()) {
      showToast('Set allowed recipients or enable dynamic recipients.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        monthlyLimit: Number(form.monthlyLimit) || 0,
        rateLimitPerMinute: Number(form.rateLimitPerMinute) || 0
      };
      if (form.id) {
        await gatewayApi.updateApp(payload);
        showToast('Application updated in PocketBase.');
      } else {
        await gatewayApi.createApp(payload);
        showToast('Application created in PocketBase.');
      }
      setEditorOpen(false);
      await loadData();
    } catch (err) {
      showToast(err.message || 'Unable to save application.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Applications</h2>
          <p className="text-sm text-slate-500">One guided setup creates or reuses its business, app, private key and starter template.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadData} className="px-3 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50">
            <SafeIcon icon={FiRefreshCw} />
          </button>
          <button onClick={()=>navigate('/connect-app')} className="px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 shadow-sm flex items-center gap-2">
            <SafeIcon icon={FiPlus}/> Connect an app
          </button>
          <button onClick={openCreate} disabled={!clients.length} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-bold text-slate-600 disabled:opacity-50">Register only (advanced)</button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex gap-3 text-sm">
          <SafeIcon icon={FiAlertCircle} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!clients.length && !loading && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 text-sm">
          Start with Connect business + app above. The wizard will create the client automatically.
        </div>
      )}

      <div className="flex bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex-1 flex items-center px-3 gap-2">
          <SafeIcon icon={FiGlobe} className="text-slate-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search apps, clients, origins or provider..."
            className="w-full text-sm outline-none bg-transparent py-1"
          />
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
          Loading applications from PocketBase...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredApps.map(app => (
            <div key={app.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col">
              <div className="p-5 flex-1">
                <div className="flex justify-between gap-4 items-start">
                  <div className="flex gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                      <SafeIcon icon={FiActivity} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-slate-900 truncate">{app.name}</h3>
                      <p className="text-xs text-slate-500 truncate">{clientMap[app.clientId] || 'Unknown Client'}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${app.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                    {app.status}
                  </span>
                </div>

                <div className="mt-5 space-y-3 text-xs">
                  <InfoRow label="Origins" value={app.origins || 'Not configured'} />
                  <InfoRow label="Provider" value={app.provider || 'Emailit'} />
                  <InfoRow label="Recipients" value={app.allowAnyRecipient ? 'Dynamic allowed' : app.allowedRecipients || 'Not configured'} />
                  <InfoRow label="Rate Limit" value={`${app.rateLimitPerMinute || 30}/min`} />
                </div>
              </div>

              <div className="p-4 border-t border-slate-100 grid grid-cols-2 gap-3 bg-slate-50/50 rounded-b-2xl">
                <button onClick={() => navigate('/connect-app?app='+encodeURIComponent(app.id))} className="py-2 bg-slate-900 text-white text-[11px] font-bold rounded-lg hover:bg-slate-800 flex items-center justify-center gap-1.5">
                  <SafeIcon icon={FiSettings} /> Guided Config
                </button>
                <button onClick={() => navigate('/logs')} className="py-2 bg-white border border-slate-200 text-slate-700 text-[11px] font-bold rounded-lg hover:bg-slate-50 flex items-center justify-center gap-1.5">
                  <SafeIcon icon={FiActivity} /> Logs
                </button>
                <button onClick={()=>openEdit(app)} className="col-span-2 flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white py-2 text-xs font-bold text-slate-600 hover:bg-slate-100">Advanced settings</button>
                <button onClick={()=>navigate('/connect-app?app='+encodeURIComponent(app.id))} className="col-span-2 flex items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 text-xs font-bold text-white hover:bg-blue-700">
                <SafeIcon icon={FiCopy}/> Continue setup or test
              </button>
              
              </div>
            </div>
          ))}

          {!filteredApps.length && (
            <div className="md:col-span-2 xl:col-span-3 bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
              No applications found.
            </div>
          )}
        </div>
      )}

      {editorOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 bg-slate-900 text-white flex justify-between items-center">
              <div>
                <h3 className="font-bold">{form.id ? 'Configure Application' : 'Register Application'}</h3>
                <p className="text-xs text-slate-400">These policies are enforced by the BPA Gateway before Emailit is called.</p>
              </div>
              <button onClick={() => setEditorOpen(false)} className="p-1 hover:bg-slate-800 rounded">
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="App Name *" value={form.name} onChange={value => setForm({ ...form, name: value })} />
                <Select label="Client *" value={form.clientId} onChange={value => setForm({ ...form, clientId: value })}>
                  {clients.map(client => <option key={client.id} value={client.id}>{client.name}</option>)}
                </Select>
                <Select label="Status" value={form.status} onChange={value => setForm({ ...form, status: value })}>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="suspended">Suspended</option>
                </Select>
                <Field label="Provider" value={form.provider} onChange={value => setForm({ ...form, provider: value })} />
              </div>

              <section className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
                <div className="flex gap-3">
                  <SafeIcon icon={FiShield} className="text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-blue-900 text-sm">Gateway Security Policy</h4>
                    <p className="text-xs text-blue-700 mt-1">
                      Origins are checked before sending. Recipient restrictions reduce the impact of a leaked browser-visible BPA key.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                  <Field label="Allowed Origins *" value={form.origins} onChange={value => setForm({ ...form, origins: value })} placeholder="https://example.com, https://www.example.com" />
                  <Field label="Allowed Recipients" value={form.allowedRecipients} onChange={value => setForm({ ...form, allowedRecipients: value })} placeholder="owner@example.com, admin@example.com" />
                  <Field label="Rate Limit / Minute" type="number" value={form.rateLimitPerMinute} onChange={value => setForm({ ...form, rateLimitPerMinute: value })} />
                  <Field label="Monthly App Limit" type="number" value={form.monthlyLimit} onChange={value => setForm({ ...form, monthlyLimit: value })} />
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Check
                    checked={form.allowAnyRecipient}
                    onChange={checked => setForm({ ...form, allowAnyRecipient: checked })}
                    title="Allow Dynamic Recipients"
                    description="Required for OTP/customer acknowledgement patterns. Use only when the workflow genuinely needs variable recipients."
                  />
                  <Check
                    checked={form.allowDynamicReplyTo}
                    onChange={checked => setForm({ ...form, allowDynamicReplyTo: checked })}
                    title="Allow Dynamic Reply-To"
                    description="Useful for contact forms where Reply-To should be the visitor's email."
                  />
                </div>
              </section>

              <section>
                <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Sender Identity</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="From Name" value={form.fromName} onChange={value => setForm({ ...form, fromName: value })} />
                  <Field label="From Email" type="email" value={form.fromEmail} onChange={value => setForm({ ...form, fromEmail: value })} />
                  <Field label="Default Reply-To" type="email" value={form.replyToEmail} onChange={value => setForm({ ...form, replyToEmail: value })} />
                </div>
              </section>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 w-full h-24 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-blue-500" />
              </div>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={() => setEditorOpen(false)} className="px-4 py-2 text-sm font-bold text-slate-500">Cancel</button>
              <button onClick={saveApp} disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50">
                <SafeIcon icon={FiSave} /> {saving ? 'Saving...' : 'Save Application'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', placeholder = '' }) => (
  <div>
    <div className="flex items-center gap-1"><span className="text-xs font-bold text-slate-500 uppercase">{label}</span><HelpHint label={label}/></div>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
    />
  </div>
);

const Select = ({ label, value, onChange, children }) => (
  <div>
    <div className="flex items-center gap-1"><span className="text-xs font-bold text-slate-500 uppercase">{label}</span><HelpHint label={label}/></div>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm outline-none">
      {children}
    </select>
  </div>
);

const Check = ({ checked, onChange, title, description }) => (
  <div className="flex items-start gap-2"><label className="flex flex-1 gap-3 bg-white border border-blue-100 rounded-xl p-4 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1" />
    <div>
      <p className="text-xs font-bold text-slate-800">{title}</p>
      <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">{description}</p>
    </div>
  </label><HelpHint label={title}/></div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-slate-500">{label}</span>
    <span className="font-bold text-slate-800 text-right break-all">{value}</span>
  </div>
);

export default Apps;